import type { i18n as I18n } from 'i18next';
import { Duration } from 'luxon';
import { ColumnMetaType } from '@api/models/analytics/ColumnMetaDto';
import { formatDate } from '../../utils/date';

export const EMPTY_CELL = '—';

/** Sentinel column key used by the BE to mark the row's group label column. */
export const GROUP_COLUMN_KEY = 'group';

const formatDurationSec = (totalSec: number, i18n: I18n): string => {
  const sec = Math.max(0, Math.floor(totalSec));
  const d = Duration.fromObject({ seconds: sec }).shiftTo('days', 'hours', 'minutes', 'seconds');
  if (sec < 86400) return d.toFormat('hh:mm:ss');
  return i18n.t('analytics.duration.daysHoursMinutes', { days: d.days, hours: d.hours, minutes: d.minutes });
};

export const formatCellValue = (value: unknown, type: ColumnMetaType, i18n: I18n): string => {
  if (type === 'STRING') {
    return value == null || value === '' ? EMPTY_CELL : String(value);
  }
  if (type === 'BOOLEAN') {
    if (value == null) return EMPTY_CELL;
    return i18n.t(value ? 'common.yes' : 'common.no');
  }
  if (type === 'INSTANT') {
    if (value == null || value === '') return EMPTY_CELL;
    const asString = typeof value === 'string' ? value : String(value);
    return formatDate(i18n, asString, true) || EMPTY_CELL;
  }
  if (type === 'DATE') {
    if (value == null || value === '') return EMPTY_CELL;
    const asString = typeof value === 'string' ? value : String(value);
    return formatDate(i18n, asString, false) || EMPTY_CELL;
  }
  if (value == null) return EMPTY_CELL;
  const num = Number(value);
  if (Number.isNaN(num)) return EMPTY_CELL;
  switch (type) {
    case 'INT':
      return num.toLocaleString(i18n.language);
    case 'DECIMAL':
      return num.toLocaleString(i18n.language, { maximumFractionDigits: 2 });
    case 'DURATION_SEC':
      return formatDurationSec(num, i18n);
  }
};

export const isNumericColumn = (type: ColumnMetaType): boolean => type === 'INT' || type === 'DECIMAL' || type === 'DURATION_SEC';

export const getColumnAlign = (type: ColumnMetaType): 'left' | 'center' | 'right' => (isNumericColumn(type) ? 'center' : 'left');
